import {
  Building2,
  CalendarClock,
  ClipboardList,
  Wallet,
  CreditCard,
  BarChart3,
  Heart,
  Activity,
  Shield,
  Users,
  Award,
  BookOpen,
} from 'lucide-react';
import { motion } from 'framer-motion';
import type { TenantHomePageContent } from '../../types/site-pages';

interface FeaturesProps {
  mode?: 'global' | 'tenant';
  tenantContent?: TenantHomePageContent | null;
}

const globalFeatures = [
  {
    icon: Building2,
    title: 'Múltiplas Unidades, Um Painel',
    description:
      'Gerencie várias clínicas ou equipes com total isolamento de dados e controle centralizado. Cada unidade opera de forma independente, mas você tem visibilidade total.',
    points: [
      'Isolamento completo de dados entre unidades (multi-tenant).',
      'Troca rápida de contexto sem perder o fluxo de trabalho.',
      'Controle de acesso por perfil: admin, fisioterapeuta, médico e paciente.',
    ],
  },
  {
    icon: CalendarClock,
    title: 'Agenda Inteligente e Integrada',
    description:
      'Do agendamento à alta, acompanhe toda a jornada do paciente. Reduza faltas com notificações automáticas e controle inteligente de presença.',
    points: [
      'Visão por profissional com disponibilidade em tempo real.',
      'Regra de 3 strikes: cobrança automática a partir da 3ª falta.',
      'Integração com calendário Outlook e notificações via WhatsApp.',
    ],
  },
  {
    icon: ClipboardList,
    title: 'Prontuário Eletrônico Completo',
    description:
      'Registre avaliações, evoluções e planos terapêuticos em conformidade total com o COFFITO 414/2012. Histórico clínico consolidado e acessível para toda a equipe.',
    points: [
      '8 elementos obrigatórios do COFFITO garantidos automaticamente.',
      'Avaliações funcionais (TC6, espirometria) com cálculos integrados.',
      'Assinatura digital com certificado ICP-Brasil.',
    ],
  },
  {
    icon: Wallet,
    title: 'Financeiro Sem Retrabalho',
    description:
      'Concilie operação clínica e financeiro no mesmo sistema. Monitore pendências, recebimentos e repasses com clareza total.',
    points: [
      'Vinculação automática: agenda → evolução → fatura.',
      'Fechamento mensal automatizado com dashboard analítico.',
      'Alertas proativos de vencimento e inadimplência.',
    ],
  },
  {
    icon: CreditCard,
    title: 'Pacotes e Faturamento Flexível',
    description:
      'Modele pacotes de sessões, controle consumo e gere faturas automaticamente. Suporte completo ao ciclo financeiro de cada paciente.',
    points: [
      'Pacotes com validade, sessões extras e renovação automática.',
      'Sessões avulsas com preço individual por paciente.',
      'Geração automática de faturas e exportação em Excel/CSV.',
    ],
  },
  {
    icon: BarChart3,
    title: 'Dashboards e Indicadores',
    description:
      'Tome decisões baseadas em dados. Acompanhe produtividade, receita e evolução da operação com relatórios visuais e intuitivos.',
    points: [
      'Indicadores de produtividade por equipe e período.',
      'Métricas financeiras com visão de evolução mensal.',
      'Relatórios de qualidade clínica e progresso dos pacientes.',
    ],
  },
];

const tenantFeatureIconMap = {
  heart: Heart,
  activity: Activity,
  shield: Shield,
  users: Users,
  award: Award,
  'book-open': BookOpen,
} as const;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Features = ({ mode = 'global', tenantContent }: FeaturesProps) => {
  if (mode === 'tenant') {
    const title = tenantContent?.features?.title || 'Por que escolher nossa clínica?';
    const subtitle =
      tenantContent?.features?.subtitle ||
      'Serviços de fisioterapia de alta qualidade com foco na recuperação e bem-estar dos pacientes.';
    const featureItems = tenantContent?.features?.items ?? [];

    return (
      <section id="funcionalidades" className="py-20 bg-neutral-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-4">
              {title}
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {featureItems.map((feature, index) => {
              const IconComponent =
                tenantFeatureIconMap[
                  (feature.iconKey as keyof typeof tenantFeatureIconMap) || 'heart'
                ] || Heart;

              return (
              <motion.div
                key={feature.id || index}
                className="bg-white p-8 rounded-lg shadow-sm border border-neutral-200 transition-transform hover:shadow-md hover:-translate-y-1"
                variants={itemVariants}
              >
                <div className="mb-4">
                  <IconComponent className="w-10 h-10 text-primary-600" />
                </div>
                <h3 className="text-xl font-display font-semibold text-neutral-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-neutral-600">{feature.description}</p>
              </motion.div>
            )})}
          </motion.div>
        </div>
      </section>
    );
  }

  /* ─── GLOBAL MODE: Features para venda de licenças ─── */
  return (
    <section id="funcionalidades" className="py-20 md:py-28 bg-neutral-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex rounded-full bg-primary-50 border border-primary-200 px-3 py-1 text-sm font-medium text-primary-700 mb-4"
          >
            Funcionalidades
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-4"
          >
            Tudo que sua clínica precisa,{' '}
            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              integrado de verdade
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-neutral-600"
          >
            Cada módulo do RevaHub foi desenhado para funcionar em conjunto,
            eliminando retrabalho e criando um fluxo contínuo do agendamento ao
            faturamento.
          </motion.p>
        </div>

        <div className="space-y-2">
          {globalFeatures.map((feature, index) => {
            const Icon = feature.icon;
            const reverse = index % 2 !== 0;
            return (
              <section
                key={feature.title}
                className="border-t border-neutral-200 py-12 md:py-16"
              >
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.55 }}
                    className={reverse ? 'lg:order-2' : ''}
                  >
                    <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 mb-2">
                      <span className="h-6 w-6 rounded-md bg-primary-50 flex items-center justify-center text-xs">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </span>
                    <h3 className="mt-2 text-2xl md:text-3xl font-display font-bold text-neutral-900">
                      {feature.title}
                    </h3>
                    <p className="mt-4 text-lg text-neutral-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.25 }}
                    transition={{ duration: 0.55, delay: 0.1 }}
                    className={`rounded-2xl border border-neutral-200 bg-white p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-300 ${
                      reverse ? 'lg:order-1' : ''
                    }`}
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50">
                      <Icon className="h-6 w-6 text-primary-600" />
                    </div>
                    <ul className="mt-6 space-y-3">
                      {feature.points.map((point) => (
                        <li
                          key={point}
                          className="flex items-start text-neutral-700"
                        >
                          <span className="mt-1.5 mr-3 h-2 w-2 rounded-full bg-primary-500 shrink-0" />
                          <span className="leading-relaxed">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Features;
